/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, CheckCircle, XCircle, Tag, AlertCircle } from 'lucide-react';
import { ref as dbRef, get } from 'firebase/database';
import { httpsCallable } from 'firebase/functions';
import { database, functions } from '../config/firebase';
import { calculateBookingPrice, datesOverlap, getEffectiveEndDate, parseLocalDate } from '../utils/helpers';
import { STATI_BLOCCANTI } from '../config/constants';
import { useSettings } from '../hooks/useSettings';
import { useCompany } from '../contexts/CompanyContext';
import { useModal } from '../hooks/useModal';
import { useToast } from './ToastProvider';
import AvailabilityCalendar from './AvailabilityCalendar';
import TermsAndConditionsPopup from './TermsAndConditionsPopup';
import StripePaymentForm from './StripePaymentForm';
import PuntiRitiroPicker from './PuntiRitiroPicker';
import { SEDE_ID, buildSedePoint, getActivePunti, computeSupplements, composeTotalCents, centsToEuros } from '../utils/puntiRitiro.generated';

const BLU = '#2563EB';

const BookingModal = ({
  car, user, userProfile, onClose, onSuccess, allBookings,
  // Punti scelti nel catalogo pubblico (opzionali): precompilano il blocco
  // ritiro/riconsegna. Se non più validi, l'effect sotto torna alla sede.
  initialRitiroId, initialRiconsegnaId,
  // Props opzionali per il flusso di prenotazione pubblico (cliente che
  // prenota dalla pagina di un'azienda di cui NON è admin — in quel caso
  // useCompany().companyId è null, perché si basa su adminIndex/{uid}).
  // Se companyId viene passato esplicitamente, ha sempre la priorità sul
  // context — questo non cambia nulla per gli usi esistenti lato admin
  // (cassa/gestionale), che continuano a non passare questa prop.
  companyId: companyIdProp,
  companySlug,
  companyDisplayName,
}) => {
  const { companyId: companyIdFromContext } = useCompany();
  const companyId = companyIdProp || companyIdFromContext;
  const { settings, loading: settingsLoading } = useSettings(companyId);
  const toast = useToast();
  const nome = userProfile?.nome || '', cognome = userProfile?.cognome || '', telefono = userProfile?.telefono || '';
  // Difesa in profondità: PublicCarList apre questo modal solo a profilo completo,
  // ma se arrivasse qui un profilo senza questi dati il cliente pagherebbe una
  // prenotazione con contatto vuoto. Il blocco avviene PRIMA di avviare Stripe.
  const missingProfileData = !String(nome).trim() || !String(cognome).trim() || !String(telefono).trim();

  const [dataInizio, setDataInizio] = useState('');
  const [dataFine, setDataFine] = useState('');
  const [checking, setChecking] = useState(false);
  const [dateConflict, setDateConflict] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [showTermsPopup, setShowTermsPopup] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [isProcessingBooking, setIsProcessingBooking] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successBookingData, setSuccessBookingData] = useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountError, setDiscountError] = useState('');
  const [bookingError, setBookingError]   = useState('');
  const [checkingDiscount, setCheckingDiscount] = useState(false);

  // ── Punti di ritiro/riconsegna ─────────────────────────────────────────
  // Il cliente sceglie SOLO tra la sede e i punti attivi del noleggiatore.
  // `puntiLive` è una rilettura una tantum usata dopo un errore "punto non
  // più disponibile" (vedi handlePointUnavailable), per essere certi di
  // mostrare la lista aggiornata anche se il listener realtime fosse in ritardo.
  const [ritiroId, setRitiroId] = useState(initialRitiroId || SEDE_ID);
  const [riconsegnaId, setRiconsegnaId] = useState(initialRiconsegnaId || SEDE_ID);
  const [puntiLive, setPuntiLive] = useState(null);
  // Contesto del pagamento "congelato" nel momento in cui parte (importo e
  // dettagli con gli id dei punti): il PaymentIntent viene creato UNA volta con
  // questi valori. Se mentre il cliente è sul form carta il noleggiatore
  // disattiva un punto, i valori "vivi" cambiano (reset alla sede, supplemento
  // ricalcolato) ma il pagamento resta quello iniziale: importo mostrato e id
  // inviati a confirmOnlineBooking devono restare coerenti con l'addebito.
  const paymentFrozenRef = useRef(null);
  const sede = buildSedePoint(settings?.azienda);
  const activePunti = getActivePunti(puntiLive ?? settings?.puntiRitiroRiconsegna);
  const hasPunti = activePunti.length > 0;
  // Se non ci sono punti attivi la UI resta identica a prima (nessun blocco).
  const ritiroEffective = hasPunti ? ritiroId : SEDE_ID;
  const riconsegnaEffective = hasPunti ? riconsegnaId : SEDE_ID;

  // Un punto scelto che nel frattempo viene disattivato/eliminato dal
  // noleggiatore (il listener è realtime) torna alla sede, con avviso.
  useEffect(() => {
    // Finché le impostazioni non sono caricate la lista punti è vuota: non
    // va scambiata per "punto eliminato" (azzererebbe la scelta fatta nel catalogo).
    // Mentre il form di pagamento è aperto la scelta non si tocca (vedi
    // paymentFrozenRef); il controllo riparte appena si chiude. A prenotazione
    // già confermata non serve (e l'avviso "punto non più disponibile" accanto
    // a un successo sarebbe fuorviante).
    if (settingsLoading || showPayment || showSuccessPopup) return;
    const ids = new Set(activePunti.map((p) => p.id));
    let reset = false;
    if (ritiroId !== SEDE_ID && !ids.has(ritiroId)) { setRitiroId(SEDE_ID); reset = true; }
    if (riconsegnaId !== SEDE_ID && !ids.has(riconsegnaId)) { setRiconsegnaId(SEDE_ID); reset = true; }
    if (reset) toast.warning('Un punto di ritiro/riconsegna selezionato non è più disponibile: abbiamo reimpostato la sede. Controlla la scelta.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.puntiRitiroRiconsegna, puntiLive, settingsLoading, showPayment, showSuccessPopup]);

  const checkDatesAvailability = useCallback((start, end) => {
    if (!start || !end) { setDateConflict(null); return; }
    setChecking(true); setDateConflict(null);
    const id = setTimeout(() => {
      if (new Date(end) <= new Date(start)) { setDateConflict({ message: 'La data di fine deve essere successiva alla data di inizio' }); setChecking(false); return; }
      const conflict = allBookings.find(b => b.carId === car.id && STATI_BLOCCANTI.includes(b.stato) && datesOverlap(start, end, b.dataInizio, getEffectiveEndDate(b)));
      if (conflict) setDateConflict({ message: `Auto non disponibile per il periodo selezionato. Prenotata dal ${parseLocalDate(conflict.dataInizio).toLocaleDateString('it-IT')} al ${parseLocalDate(conflict.dataFine).toLocaleDateString('it-IT')}`, booking: conflict });
      setChecking(false);
    }, 300);
    return id;
  }, [allBookings, car.id]);

  useEffect(() => {
    if (dataInizio && dataFine) { const id = checkDatesAvailability(dataInizio, dataFine); return () => clearTimeout(id); }
  }, [dataInizio, dataFine, checkDatesAvailability]);

  const handleDateSelection = (start, end) => { setDataInizio(start || ''); setDataFine(end || ''); };

  let numGiorni = 0, prezzoBase = 0;
  if (dataInizio && dataFine) { numGiorni = Math.ceil((parseLocalDate(dataFine) - parseLocalDate(dataInizio)) / 86400000); prezzoBase = parseFloat(calculateBookingPrice(car, dataInizio, dataFine)); }

  const validateDiscountCode = async (code) => {
    setCheckingDiscount(true); setDiscountError('');
    try {
      const snap = await get(dbRef(database, `companies/${companyId}/discounts/${code.toUpperCase()}`));
      if (!snap.exists()) { setDiscountError('Codice sconto non valido'); setCheckingDiscount(false); return; }
      const d = snap.val();
      if (!d.attivo) { setDiscountError('Codice sconto non attivo'); setCheckingDiscount(false); return; }
      // BUG FIX: 'dataFine' è una data-only string (YYYY-MM-DD); confrontarla
      // con 'new Date(d.dataFine) < new Date()' la rende scaduta dalla
      // mezzanotte UTC di quel giorno (per l'Italia, dalle prime ore del
      // mattino) invece che a fine giornata — vedi stesso fix in
      // src/utils/bookingUtils.js e functions/index.js:reserveDiscountServer.
      if (d.dataFine) {
        const endOfExpiryDay = parseLocalDate(d.dataFine);
        endOfExpiryDay.setHours(23, 59, 59, 999);
        if (endOfExpiryDay < new Date()) { setDiscountError('Codice sconto scaduto'); setCheckingDiscount(false); return; }
      }
      if (d.utilizziMassimi && d.utilizziAttuali >= d.utilizziMassimi) { setDiscountError('Codice sconto esaurito'); setCheckingDiscount(false); return; }
      if (d.importoMinimo && prezzoBase < d.importoMinimo) { setDiscountError(`Importo minimo richiesto: €${d.importoMinimo}`); setCheckingDiscount(false); return; }
      setAppliedDiscount({ code: code.toUpperCase(), type: d.tipo, value: d.valore, description: d.descrizione });
      setDiscountError(''); setCheckingDiscount(false);
    } catch { setDiscountError('Errore nella verifica del codice'); setCheckingDiscount(false); }
  };
  const applyDiscountCode = () => { if (!discountCode.trim()) { setDiscountError('Inserisci un codice sconto'); return; } validateDiscountCode(discountCode.trim()); };
  const removeDiscount = () => { setAppliedDiscount(null); setDiscountCode(''); setDiscountError(''); };

  let discountAmount = 0;
  if (appliedDiscount) { discountAmount = appliedDiscount.type === 'percentage' ? (prezzoBase * appliedDiscount.value) / 100 : appliedDiscount.value; discountAmount = Math.min(discountAmount, prezzoBase); }
  // Supplementi (anteprima: il server ricalcola tutto). Sconto e importo
  // minimo riguardano solo il noleggio; i supplementi si sommano dopo.
  const supplements = computeSupplements({
    punti: puntiLive ?? settings?.puntiRitiroRiconsegna,
    azienda: settings?.azienda,
    ritiroId: ritiroEffective,
    riconsegnaId: riconsegnaEffective,
  });
  const supplementoRitiro = supplements.ok ? centsToEuros(supplements.supplementoRitiroCents) : 0;
  const supplementoRiconsegna = supplements.ok ? centsToEuros(supplements.supplementoRiconsegnaCents) : 0;
  const supplementoTotale = supplementoRitiro + supplementoRiconsegna;
  const puntoRitiro = supplements.ok ? supplements.ritiro : sede;
  const puntoRiconsegna = supplements.ok ? supplements.riconsegna : sede;
  const prezzoTotale = centsToEuros(composeTotalCents({
    prezzoBase, discountAmount, supplementoTotaleCents: supplements.ok ? supplements.supplementoTotaleCents : 0,
  }));
  const isFormValid = dataInizio && dataFine && !dateConflict && !checking && prezzoTotale > 0 && supplements.ok;

  const handleProceedToPayment = () => { if (!isFormValid) return; setShowSummary(true); };
  const handleAcceptTerms = () => { setTermsAccepted(true); setShowTermsPopup(false); };
  const handleConfirmAndPay = () => {
    if (missingProfileData) {
      toast.warning('Mancano nome, cognome o telefono nel tuo profilo: completalo prima di pagare.');
      return;
    }
    if (!termsAccepted || !privacyAccepted) {
      toast.warning("Devi accettare sia i Termini e Condizioni che l'Informativa sulla Privacy per procedere.");
      return;
    }
    paymentFrozenRef.current = { amount: prezzoTotale.toFixed(2), bookingDetails: { ...bookingDetails } };
    setShowPayment(true);
  };

  // FIX (Agosto 2026, audit sicurezza): il booking non viene più creato
  // scrivendo direttamente su Realtime Database dal client. Un cliente
  // autenticato poteva farlo (era necessario per il flusso normale) senza
  // che nulla verificasse un pagamento reale dietro — bastava intercettare
  // questa funzione o usare la console del browser per crearsi una
  // prenotazione "confermata" gratis. Ora la creazione passa dalla Cloud
  // Function confirmOnlineBooking, che recupera il PaymentIntent DIRETTAMENTE
  // da Stripe (non si fida di ciò che il client dichiara), verifica che sia
  // davvero riuscito e solo allora scrive il booking con Admin SDK. Le Rules
  // (database.rules.json) sono state ristrette di conseguenza: un cliente
  // online non può più scrivere direttamente companies/{id}/bookings/*.
  const handlePaymentSuccess = async (paymentInfo) => {
    if (isProcessingBooking) return;
    setIsProcessingBooking(true); setLoading(true); setBookingError('');
    try {
      // Audit 2026-09-20, punto #2: il vero lock anti-doppia-prenotazione è
      // ora nella Cloud Function confirmOnlineBooking (lock server-side per
      // auto + rimborso automatico se il conflitto viene rilevato lì). PRIMA
      // di questo fix, un lock client-side qui — se occupato — interrompeva
      // il flusso PRIMA di chiamare la function: il pagamento Stripe era già
      // andato a buon fine, ma senza prenotazione creata e SENZA rimborso
      // automatico (il rimborso viene emesso solo dentro la Cloud Function).
      // Chiamare sempre e comunque la function, lasciando che sia lei a
      // decidere se c'è conflitto, garantisce che ogni pagamento riuscito
      // finisca o in una prenotazione o in un rimborso — mai in un limbo.
      const confirmOnlineBookingFn = httpsCallable(functions, 'confirmOnlineBooking');
      const result = await confirmOnlineBookingFn({
        companyId,
        carId: car.id,
        dataInizio,
        dataFine,
        paymentIntentId: paymentInfo?.transactionId,
        discountCode: appliedDiscount?.code || null,
        nomeCliente: nome,
        cognomeCliente: cognome,
        telefonoCliente: telefono,
        companySlug: companySlug || null,
        companyDisplayName: companyDisplayName || null,
        privacyAccepted: true,
        puntoRitiroId: (paymentFrozenRef.current?.bookingDetails ?? bookingDetails).puntoRitiroId,
        puntoRiconsegnaId: (paymentFrozenRef.current?.bookingDetails ?? bookingDetails).puntoRiconsegnaId,
      });
      const { bookingId, prezzoTotale: prezzoTotaleServer, giorni: giorniServer, luogoRitiro: luogoRitiroServer, luogoRiconsegna: luogoRiconsegnaServer, supplementoTotale: supplementoTotaleServer } = result.data;
      setSuccessBookingData({
        bookingId, car: `${car.marca} ${car.modello}`, dataInizio, dataFine,
        prezzoTotale: Number(prezzoTotaleServer ?? prezzoTotale).toFixed(2), giorni: giorniServer ?? numGiorni,
        // Dal server (snapshot definitivo); mai dal client.
        luogoRitiro: luogoRitiroServer || null,
        luogoRiconsegna: luogoRiconsegnaServer || null,
        supplementoTotale: Number(supplementoTotaleServer || 0),
      });
      setShowPayment(false); setShowSuccessPopup(true);
    } catch (error) {
      let msg = 'Errore nella creazione della prenotazione. Riprova tra poco.';
      if (error.code === 'functions/failed-precondition') msg = error.message || 'Il pagamento non è stato verificato correttamente.';
      else if (error.code === 'functions/aborted') msg = error.message || "L'auto non è più disponibile per queste date.";
      else if (error.code === 'PERMISSION_DENIED') msg = 'Errore di permessi. Riprova o contatta il noleggiatore.';
      else if (error.message?.includes('network')) msg = 'Errore di connessione. Verifica la tua connessione internet.';
      // Un alert() nativo qui sarebbe bruttissimo su mobile (blocca tutto lo
      // schermo, non è stilizzabile) e mostrare error.message grezzo a un
      // cliente che ha appena pagato è pessima UX — quel dettaglio resta
      // solo in console per il debug, mai a schermo.
      console.error('Errore creazione prenotazione:', error);
      setBookingError(msg);
    } finally {
      setLoading(false); setIsProcessingBooking(false);
    }
  };

  // Un punto scelto non è più valido lato server (disattivato/eliminato
  // mentre il cliente compilava): si torna alla scelta con la lista
  // riletta dal database, invece di lasciare il cliente davanti a un errore.
  const handlePointUnavailable = async (message) => {
    setShowPayment(false); setShowSummary(false);
    try {
      const snap = await get(dbRef(database, `companies/${companyId}/settings/puntiRitiroRiconsegna`));
      setPuntiLive(snap.exists() ? snap.val() : {});
    } catch { /* il listener realtime resta comunque attivo */ }
    setBookingError(message || 'Un punto di ritiro/riconsegna non è più disponibile. Controlla la scelta e riprova.');
  };

  const bookingDetails = {
    carName: `${car.marca} ${car.modello}`, carId: car.id, companyId, dataInizio, dataFine, giorni: numGiorni,
    discountCode: appliedDiscount?.code, discountAmount,
    puntoRitiroId: ritiroEffective, puntoRiconsegnaId: riconsegnaEffective,
    // Solo per mostrare il riepilogo nel form di pagamento; il server non li legge.
    luogoRitiroLabel: hasPunti ? puntoRitiro.nome : null,
    luogoRiconsegnaLabel: hasPunti ? puntoRiconsegna.nome : null,
    prezzoBase, supplementoRitiro, supplementoRiconsegna,
  };
  const ctaLabel = !dataInizio || !dataFine ? 'Seleziona le date' : dateConflict ? 'Date non disponibili' : checking ? 'Controllo disponibilita...' : loading ? 'Attendere...' : 'Procedi al Pagamento';

  // Audit 2026-09-20, punto #11: accessibilità (Escape, focus trap, blocco
  // scroll pagina, ritorno del focus alla chiusura).
  const modalRef = useModal(onClose);

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
        <div ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="booking-modal-title" className="bg-white w-full sm:max-w-2xl sm:rounded-2xl shadow-lg max-h-[95vh] sm:max-h-[92vh] overflow-y-auto flex flex-col rounded-t-2xl">
          <div className="sticky top-0 z-10 flex-shrink-0 rounded-t-2xl" style={{ background: BLU }}>
            <div className="px-6 py-5 flex justify-between items-center">
              <div>
                <h2 id="booking-modal-title" className="text-xl sm:text-2xl font-black text-white tracking-tight">Prenota Ora</h2>
                <p className="text-sm text-white opacity-70 mt-0.5">{car.marca} {car.modello} — {car.categoria}</p>
              </div>
              <button onClick={onClose} className="w-11 h-11 flex items-center justify-center rounded-xl bg-white bg-opacity-10 hover:bg-opacity-20 transition text-white"><X className="w-5 h-5" /></button>
            </div>
          </div>

          <div className="p-4 sm:p-6 flex-1">
            {!showSummary ? (
              <>
                <AvailabilityCalendar car={car} allBookings={allBookings} onSelectDates={handleDateSelection} />

                {dateConflict && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div><p className="font-bold text-red-700 text-sm">Date non disponibili</p><p className="text-xs text-red-600 mt-0.5">{dateConflict.message}</p></div>
                  </div>
                )}

                {dataInizio && dataFine && !dateConflict && (
                  <div className="mb-4 p-4 bg-slate-50 border border-slate-200 rounded-2xl">
                    <div className="flex items-center gap-2 mb-3"><CheckCircle className="w-4 h-4 text-green-500" /><span className="font-bold text-slate-800 text-sm">Date Disponibili!</span></div>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div className="bg-white rounded-xl p-3 border border-slate-200"><p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-1">Ritiro</p><p className="font-bold text-slate-800 text-sm">{parseLocalDate(dataInizio).toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short' })}</p></div>
                      <div className="bg-white rounded-xl p-3 border border-slate-200"><p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-1">Riconsegna</p><p className="font-bold text-slate-800 text-sm">{parseLocalDate(dataFine).toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short' })}</p></div>
                    </div>
                    <div className="bg-white rounded-xl p-3 border border-slate-200 flex items-center justify-between">
                      <span className="text-sm text-slate-600">{numGiorni} {numGiorni === 1 ? 'giorno' : 'giorni'}</span>
                      <span className="text-xl font-black tracking-tight" style={{ color: BLU }}>€{prezzoBase.toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {dataInizio && dataFine && !dateConflict && hasPunti && (
                  <>
                    <PuntiRitiroPicker
                      sede={sede} punti={activePunti}
                      ritiroId={ritiroEffective} riconsegnaId={riconsegnaEffective}
                      onChange={({ ritiroId: r, riconsegnaId: c }) => { if (r !== undefined) setRitiroId(r); if (c !== undefined) setRiconsegnaId(c); }}
                    />
                    {supplementoTotale > 0 && (
                      <div className="mb-4 px-4 py-3 bg-white border border-slate-200 rounded-2xl flex items-center justify-between text-sm">
                        <span className="text-slate-600">Totale con supplementi</span>
                        <span className="text-lg font-black tracking-tight" style={{ color: BLU }}>€{prezzoTotale.toFixed(2)}</span>
                      </div>
                    )}
                  </>
                )}

                <button onClick={handleProceedToPayment} disabled={!isFormValid || loading} className="w-full py-4 rounded-2xl font-black text-base tracking-tight transition"
                  style={isFormValid && !loading ? { background: BLU, color: '#fff' } : { background: '#E2E8F0', color: '#94A3B8', cursor: 'not-allowed' }}>
                  {ctaLabel}
                </button>
              </>
            ) : (
              <div className="space-y-3">
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200">
                  <p className="font-black text-slate-900 tracking-tight">{car.marca} {car.modello}</p>
                  <p className="text-sm text-slate-500 capitalize mt-0.5">{car.categoria}</p>
                  {car.targa && <span className="inline-block mt-2 text-xs font-mono font-bold px-2.5 py-1 rounded-lg border border-slate-300 text-slate-600 bg-white">Targa: {car.targa}</span>}
                </div>

                <div className="bg-white rounded-2xl p-4 border border-slate-200">
                  <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3">Dati Cliente</p>
                  <div className="space-y-1 text-sm text-slate-700">
                    <p><span className="font-bold">Nome:</span> {nome} {cognome}</p>
                    <p><span className="font-bold">Tel:</span> {telefono}</p>
                    <p className="truncate"><span className="font-bold">Email:</span> {user.email}</p>
                  </div>
                  {missingProfileData && (
                    <p className="mt-3 text-xs text-red-600" data-testid="profilo-incompleto">
                      Mancano nome, cognome o telefono nel tuo profilo. Completa il profilo dall'area cliente, poi riprova a prenotare.
                    </p>
                  )}
                </div>

                <div className="bg-white rounded-2xl p-4 border border-slate-200">
                  <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3">Periodo Noleggio</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div><p className="text-xs text-slate-400 mb-0.5">Ritiro</p><p className="font-bold text-slate-800 text-sm">{parseLocalDate(dataInizio).toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}</p></div>
                    <div><p className="text-xs text-slate-400 mb-0.5">Riconsegna</p><p className="font-bold text-slate-800 text-sm">{parseLocalDate(dataFine).toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}</p></div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-100"><p className="text-xs text-slate-400 mb-0.5">Durata</p><p className="font-bold text-slate-800 text-sm">{numGiorni} {numGiorni === 1 ? 'giorno' : 'giorni'}</p></div>
                  {hasPunti && (
                    <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="riepilogo-punti">
                      <div><p className="text-xs text-slate-400 mb-0.5">Punto di ritiro</p><p className="font-bold text-slate-800 text-sm">{puntoRitiro.nome}</p>{puntoRitiro.indirizzo && <p className="text-xs text-slate-500">{puntoRitiro.indirizzo}</p>}{puntoRitiro.note && <p className="text-xs italic text-slate-500">{puntoRitiro.note}</p>}</div>
                      <div><p className="text-xs text-slate-400 mb-0.5">Punto di riconsegna</p><p className="font-bold text-slate-800 text-sm">{puntoRiconsegna.nome}</p>{puntoRiconsegna.indirizzo && <p className="text-xs text-slate-500">{puntoRiconsegna.indirizzo}</p>}{puntoRiconsegna.note && <p className="text-xs italic text-slate-500">{puntoRiconsegna.note}</p>}</div>
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-2xl p-4 border border-slate-200">
                  <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3 flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" style={{ color: BLU }} />Codice Sconto</p>
                  {!appliedDiscount ? (
                    <div className="space-y-2">
                      <div className="flex flex-col xs:flex-row gap-2">
                        <input type="text" value={discountCode} onChange={(e) => setDiscountCode(e.target.value.toUpperCase())} placeholder="Inserisci codice"
                          className="flex-1 px-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 uppercase" maxLength={20} />
                        <button onClick={applyDiscountCode} disabled={checkingDiscount || !discountCode.trim()} className="flex items-center justify-center px-4 py-2.5 text-sm rounded-xl font-bold transition disabled:opacity-40 w-full xs:w-auto"
                          style={{ background: BLU, color: '#fff' }}>{checkingDiscount ? '...' : 'Applica'}</button>
                      </div>
                      {discountError && <div className="p-2.5 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2"><XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" /><p className="text-xs text-red-600">{discountError}</p></div>}
                    </div>
                  ) : (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-xl flex items-center justify-between">
                      <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /><div><p className="font-bold text-green-800 text-sm">{appliedDiscount.code}</p>{appliedDiscount.description && <p className="text-xs text-green-600">{appliedDiscount.description}</p>}</div></div>
                      <button onClick={removeDiscount} className="text-red-400 hover:text-red-600 p-1 transition"><X className="w-4 h-4" /></button>
                    </div>
                  )}
                </div>

                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200">
                  <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3">Dettaglio Costi</p>
                  <div className="space-y-2 mb-3 text-sm">
                    <div className="flex justify-between text-slate-700"><span>Base ({numGiorni} {numGiorni === 1 ? 'giorno' : 'giorni'})</span><span className="font-medium">€{prezzoBase.toFixed(2)}</span></div>
                    {appliedDiscount && <div className="flex justify-between text-green-600"><span className="flex items-center gap-1"><Tag className="w-3 h-3" />Sconto {appliedDiscount.code}{appliedDiscount.type === 'percentage' && ` (${appliedDiscount.value}%)`}</span><span className="font-medium">-€{discountAmount.toFixed(2)}</span></div>}
                    {supplementoRitiro > 0 && <div className="flex justify-between text-slate-700"><span>Supplemento ritiro ({puntoRitiro.nome})</span><span className="font-medium">+€{supplementoRitiro.toFixed(2)}</span></div>}
                    {supplementoRiconsegna > 0 && <div className="flex justify-between text-slate-700"><span>Supplemento riconsegna ({puntoRiconsegna.nome})</span><span className="font-medium">+€{supplementoRiconsegna.toFixed(2)}</span></div>}
                    {supplementoTotale > 0 && <p className="text-xs text-slate-400">I supplementi non sono scontabili con i codici sconto.</p>}
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                    <span className="font-bold text-slate-700">Totale</span>
                    <span className="text-2xl font-black tracking-tight" style={{ color: BLU }}>€{prezzoTotale.toFixed(2)}</span>
                  </div>
                </div>

                <div className={`p-4 rounded-2xl border-2 transition cursor-pointer ${termsAccepted ? 'border-green-300 bg-green-50' : 'border-slate-200 bg-white hover:border-blue-600'}`} onClick={() => setShowTermsPopup(true)}>
                  <div className="flex items-start gap-3">
                    <input type="checkbox" checked={termsAccepted} readOnly className="mt-0.5 w-4 h-4 rounded border-gray-300 pointer-events-none accent-blue-600" />
                    <p className="text-sm text-slate-700 select-none">Ho letto e accetto i <span className="font-bold underline" style={{ color: BLU }}>Termini e Condizioni</span>{!termsAccepted && <span className="block text-xs text-slate-400 mt-0.5">Clicca per leggere e accettare</span>}</p>
                  </div>
                </div>

                <div
                  className={`p-4 rounded-2xl border-2 transition cursor-pointer ${privacyAccepted ? 'border-green-300 bg-green-50' : 'border-slate-200 bg-white hover:border-blue-600'}`}
                  onClick={() => setPrivacyAccepted(v => !v)}
                >
                  <div className="flex items-start gap-3">
                    <input type="checkbox" checked={privacyAccepted} readOnly className="mt-0.5 w-4 h-4 rounded border-gray-300 pointer-events-none accent-blue-600" />
                    <p className="text-sm text-slate-700 select-none">
                      Ho letto e accetto l'{' '}
                      <a
                        href={companySlug ? `/${companySlug}/privacy` : '/privacy'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-bold underline"
                        style={{ color: BLU }}
                        onClick={e => e.stopPropagation()}
                      >
                        Informativa sulla Privacy
                      </a>
                      {!privacyAccepted && <span className="block text-xs text-slate-400 mt-0.5">Obbligatoria per procedere</span>}
                    </p>
                  </div>
                </div>

                <div className="flex gap-2 pt-1">
                  <button onClick={() => setShowSummary(false)} className="px-5 py-3 bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 transition font-bold text-sm">Indietro</button>
                  <button
                    onClick={handleConfirmAndPay}
                    disabled={!termsAccepted || !privacyAccepted || loading || isProcessingBooking || missingProfileData}
                    className="flex-1 py-3 rounded-xl font-black text-sm transition"
                    style={termsAccepted && privacyAccepted && !loading && !isProcessingBooking && !missingProfileData ? { background: BLU, color: '#fff' } : { background: '#E2E8F0', color: '#94A3B8', cursor: 'not-allowed' }}
                  >
                    {loading || isProcessingBooking ? 'Attendere...' : 'Conferma e Paga'}
                  </button>
                </div>
              </div>
            )}

            {bookingError && (
              <div className="mt-4 flex items-start gap-3 p-4 rounded-2xl" style={{ background: '#FEF2F2', border: '1px solid #FECACA' }}>
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{bookingError}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showTermsPopup && <TermsAndConditionsPopup settings={settings} onClose={() => setShowTermsPopup(false)} onAccept={handleAcceptTerms} />}
      {showPayment && <StripePaymentForm amount={paymentFrozenRef.current?.amount ?? prezzoTotale.toFixed(2)} stripePublicKey={settings?.payment?.stripePublicKey || ''} onSuccess={handlePaymentSuccess} onCancel={() => setShowPayment(false)} onPointUnavailable={handlePointUnavailable} bookingDetails={paymentFrozenRef.current?.bookingDetails ?? bookingDetails} />}

      {showSuccessPopup && successBookingData && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-end sm:items-center justify-center p-0 sm:p-4 z-[70]">
          <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl shadow-lg max-h-[92vh] overflow-y-auto rounded-t-2xl">
            <div className="p-6">
              <div className="text-center mb-5">
                <div className="w-16 h-16 bg-green-50 border border-green-200 rounded-2xl flex items-center justify-center mx-auto mb-4"><CheckCircle className="w-9 h-9 text-green-500" /></div>
                <h3 className="text-xl font-black text-slate-900 tracking-tight mb-1">{settings.bookingConfirmation?.titolo || 'Prenotazione Confermata!'}</h3>
                <p className="text-slate-500 text-sm">{settings.bookingConfirmation?.messaggio || 'La tua prenotazione e stata confermata con successo.'}</p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 mb-4">
                <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3">Dettagli Prenotazione</p>
                <div className="space-y-2 text-sm">
                  {[['ID', <span className="font-mono font-bold" style={{ color: BLU }}>{successBookingData.bookingId.substring(0, 8).toUpperCase()}</span>], ['Auto', <span className="font-bold text-slate-800">{successBookingData.car}</span>], car.targa ? ['Targa', <span className="font-mono font-bold" style={{ color: BLU }}>{car.targa}</span>] : null, ['Cliente', <span className="font-bold text-slate-800">{nome} {cognome}</span>], ['Dal', parseLocalDate(successBookingData.dataInizio).toLocaleDateString('it-IT')], ['Al', parseLocalDate(successBookingData.dataFine).toLocaleDateString('it-IT')], ['Durata', `${successBookingData.giorni} ${successBookingData.giorni === 1 ? 'giorno' : 'giorni'}`], successBookingData.luogoRitiro ? ['Ritiro in', <span className="text-right">{successBookingData.luogoRitiro}</span>] : null, successBookingData.luogoRiconsegna ? ['Riconsegna in', <span className="text-right">{successBookingData.luogoRiconsegna}</span>] : null, successBookingData.supplementoTotale > 0 ? ['Supplementi', `€${successBookingData.supplementoTotale.toFixed(2)}`] : null].filter(Boolean).map(([label, value], i) => (
                    <div key={i} className="flex justify-between"><span className="text-slate-400">{label}:</span><span className="text-slate-700">{value}</span></div>
                  ))}
                  <div className="flex justify-between border-t border-slate-200 pt-2 mt-1"><span className="font-bold text-slate-600">Totale:</span><span className="font-black text-lg text-green-600">€{successBookingData.prezzoTotale}</span></div>
                </div>
              </div>
              {settings.bookingConfirmation?.infoAggiuntive && <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 mb-4"><p className="text-xs text-amber-800"><strong>Info:</strong> {settings.bookingConfirmation.infoAggiuntive}</p></div>}
              {(settings.bookingConfirmation?.telefono || settings.bookingConfirmation?.email) && (
                <div className="mb-4 p-3 bg-white border border-slate-200 rounded-2xl">
                  <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-2">Hai domande?</p>
                  {settings.bookingConfirmation?.telefono && <p className="text-slate-600 text-xs">Tel: <a href={`tel:${settings.bookingConfirmation.telefono}`} className="font-bold hover:underline" style={{ color: BLU }}>{settings.bookingConfirmation.telefono}</a></p>}
                  {settings.bookingConfirmation?.email && <p className="text-slate-600 text-xs">Email: <a href={`mailto:${settings.bookingConfirmation.email}`} className="font-bold hover:underline" style={{ color: BLU }}>{settings.bookingConfirmation.email}</a></p>}
                </div>
              )}
              <button onClick={() => { setShowSuccessPopup(false); if (onSuccess) { onSuccess(); } else { onClose(); } }} className="w-full py-3.5 rounded-2xl font-black text-white tracking-tight transition hover:opacity-90" style={{ background: BLU }}>
                {settings.bookingConfirmation?.testoBottone || 'Perfetto, Grazie!'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BookingModal;
